/**
 * @fileoverview Event name, dates, costs, durations, and location fields.
 * @module pages/b2bEventForm/B2BEventDetailsSection
 */

import styles from "../../styles/B2BEventForm.module.css";
import { isAppointmentDurationValid } from "./b2bEventFormModel";

const COST_STEP = "0.01";
const COST_MIN = 0;

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const EventNameField = ({ t, formData, showError, nameValidation, onChange }) => (
  <div className={`${styles.formRow} ${styles.rowEventName}`}>
    <div className={styles.nameCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.eventName")}</label>
      <input
        type="text"
        name="nombre"
        value={formData.nombre}
        onChange={onChange}
        className={`${styles.input} ${(showError && !formData.nombre) || !nameValidation.isValid ? styles.inputError : ""}`}
        placeholder={t("pages.b2bEventForm.eventNamePlaceholder")}
      />
      {!nameValidation.isValid && (
        <div
          className={styles.validationMessage}
          style={{
            color: "red",
            fontSize: "12px",
            marginTop: "4px",
            fontWeight: "500",
          }}
        >
          {t("pages.b2bEventForm.duplicateEventNameError")}
        </div>
      )}
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const EventRangeFields = ({ t, formData, showError, onChange }) => (
  <div className={`${styles.formRow} ${styles.rowEventType}`}>
    <div className={styles.eventDateCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.eventStartDate")}</label>
      <input
        type="date"
        name="fechaInicioEvento"
        className={`${styles.input} ${showError && !formData.fechaInicioEvento ? styles.inputError : ""}`}
        value={formData.fechaInicioEvento || ""}
        onChange={onChange}
      />
    </div>
    <div className={styles.dateDivider} />
    <div className={styles.eventDateCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.eventEndDate")}</label>
      <input
        type="date"
        name="fechaFinEvento"
        className={`${styles.input} ${showError && !formData.fechaFinEvento ? styles.inputError : ""}`}
        value={formData.fechaFinEvento || ""}
        onChange={onChange}
      />
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const EventDescriptionField = ({ t, formData, showError, onChange }) => (
  <div className={styles.formRow}>
    <div className={styles.descriptionCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.description")}</label>
      <textarea
        name="descripcion"
        value={formData.descripcion}
        onChange={onChange}
        className={`${styles.textarea} ${showError && !formData.descripcion ? styles.textareaError : ""}`}
        placeholder={t("pages.b2bEventForm.descriptionPlaceholder")}
      />
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const MemberDateFields = ({ t, formData, showError, onChange }) => (
  <div className={`${styles.formRow} ${styles.rowThreeDateCols}`}>
    <div className={styles.eventDateCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.membersDate")}</label>
      <input
        type="date"
        name="fechaDisponibleSocios"
        className={`${styles.input} ${showError && !formData.fechaDisponibleSocios ? styles.inputError : ""}`}
        value={formData.fechaDisponibleSocios || ""}
        onChange={onChange}
      />
    </div>
    <div className={`${styles.eventDateCol} ${styles.nonMembersDateCol}`}>
      <label className={styles.label}>{t("pages.b2bEventForm.nonMembersDate")}</label>
      <input
        type="date"
        name="fechaDisponibleNoSocios"
        className={`${styles.input} ${showError && !formData.fechaDisponibleNoSocios ? styles.inputError : ""}`}
        value={formData.fechaDisponibleNoSocios || ""}
        onChange={onChange}
      />
    </div>
    <div className={styles.eventDateCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.dueDateBuyer")}</label>
      <input
        type="date"
        name="fechaVigenciaCompradores"
        placeholder={t("common.dateFormatPlaceholder")}
        className={`${styles.input} ${showError && !formData.fechaVigenciaCompradores ? styles.inputError : ""}`}
        value={formData.fechaVigenciaCompradores || ""}
        onChange={onChange}
      />
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const RegistrationFields = ({ t, formData, showError, onChange }) => (
  <div className={`${styles.formRow} ${styles.rowDateAndTwoTimes}`}>
    <div className={styles.eventDateCol}>
      <label className={styles.label}>
        {t("pages.b2bEventForm.dueDateRegistration")}
      </label>
      <input
        type="date"
        name="fechaLimiteRegistro"
        placeholder={t("common.dateFormatPlaceholder")}
        className={`${styles.input} ${showError && !formData.fechaLimiteRegistro ? styles.inputError : ""}`}
        value={formData.fechaLimiteRegistro || ""}
        onChange={onChange}
      />
    </div>
    <div className={styles.timeCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.startTime")}</label>
      <input
        type="time"
        name="horaInicio"
        value={formData.horaInicio || ""}
        onChange={onChange}
        className={`${styles.input} ${showError && !formData.horaInicio ? styles.inputError : ""}`}
      />
    </div>
    <div className={styles.timeCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.endTime")}</label>
      <input
        type="time"
        name="horaFin"
        value={formData.horaFin || ""}
        onChange={onChange}
        className={`${styles.input} ${showError && !formData.horaFin ? styles.inputError : ""}`}
      />
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const CostFields = ({ t, formData, onChange }) => (
  <div className={`${styles.formRow} ${styles.rowTwoCols}`}>
    <div className={styles.durationCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.membersCost")}</label>
      <input
        type="number"
        name="costoSocios"
        min={COST_MIN}
        step={COST_STEP}
        className={styles.input}
        placeholder={t("pages.b2bEventForm.costPlaceholder")}
        value={
          formData.costoSocios === null || formData.costoSocios === undefined
            ? ""
            : formData.costoSocios
        }
        onChange={onChange}
      />
    </div>
    <div className={styles.durationCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.nonMembersCost")}</label>
      <input
        type="number"
        name="costoNoSocios"
        min={COST_MIN}
        step={COST_STEP}
        className={styles.input}
        placeholder={t("pages.b2bEventForm.costPlaceholder")}
        value={
          formData.costoNoSocios === null || formData.costoNoSocios === undefined
            ? ""
            : formData.costoNoSocios
        }
        onChange={onChange}
      />
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const DurationField = ({ label, name, value, showError, onChange, t }) => (
  <div className={styles.durationCol}>
    <label className={styles.label}>{label}</label>
    <div
      className={`${styles.durationInputContainer} ${showError && !isAppointmentDurationValid(value) ? styles.durationInputError : ""}`}
    >
      <input
        type="text"
        name={name}
        pattern="([0-9]|1[0-9]|2[0-4]):[0-5][0-9]"
        value={value || "0:00"}
        onChange={onChange}
        className={styles.input}
        placeholder={t("common.durationPlaceholder")}
      />
      <span className={styles.durationUnit}>{t("pages.b2bEventForm.hoursShort")}</span>
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const DurationFields = ({ t, formData, showError, onChange }) => (
  <div className={`${styles.formRow} ${styles.rowTwoCols}`}>
    <DurationField
      t={t}
      label={t("pages.b2bEventForm.durationAppointmentsMembers")}
      name="duracionCitasSocios"
      value={formData.duracionCitasSocios}
      showError={showError}
      onChange={onChange}
    />
    <DurationField
      t={t}
      label={t("pages.b2bEventForm.durationAppointmentsNonMembers")}
      name="duracionCitasNoSocios"
      value={formData.duracionCitasNoSocios}
      showError={showError}
      onChange={onChange}
    />
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const LocationField = ({ t, formData, onChange }) => (
  <div className={styles.formRow}>
    <div className={styles.locationCol}>
      <label className={styles.label}>{t("pages.b2bEventForm.eventLocation")}</label>
      <input
        type="text"
        name="ubicacion"
        value={formData.ubicacion || ""}
        onChange={onChange}
        className={styles.input}
        placeholder={t("pages.b2bEventForm.eventLocationPlaceholder")}
      />
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const B2BEventDetailsSection = ({ t, formData, showError, nameValidation, onChange }) => (
  <div className={styles.detailsContainer}>
    <EventNameField
      t={t}
      formData={formData}
      showError={showError}
      nameValidation={nameValidation}
      onChange={onChange}
    />
    <EventRangeFields t={t} formData={formData} showError={showError} onChange={onChange} />
    <EventDescriptionField t={t} formData={formData} showError={showError} onChange={onChange} />
    <MemberDateFields t={t} formData={formData} showError={showError} onChange={onChange} />
    <RegistrationFields t={t} formData={formData} showError={showError} onChange={onChange} />
    <CostFields t={t} formData={formData} onChange={onChange} />
    <DurationFields t={t} formData={formData} showError={showError} onChange={onChange} />
    <LocationField t={t} formData={formData} onChange={onChange} />
  </div>
);

export default B2BEventDetailsSection;
